import { useState, useMemo, useCallback, memo, useRef, useEffect } from "react";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { Checkbox, Flex, Separator, Text } from "@radix-ui/themes";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  CopyIcon,
  CheckIcon,
} from "@radix-ui/react-icons";
import { twMerge } from "tailwind-merge";
import Animal from "react-animals";
import toast from "react-hot-toast";
import { useGondolaChangeGroupsData } from "../hooks";
import {
  SKUSectionChangeGroup,
  IntersectedProx,
} from "@/entities/gondola-change-groups/model/types";
import {
  setSelectedSkuSectionChangeGroupTableData,
  setSelectedSkuSectionChangeId,
  TrackAvatarBinding,
  setSelectedDetectionId,
  setClosestGondolaDistItems,
  clearClosestGondolaDistItems,
  setSelectedClosestGondolaDistItem,
  setSelectedTriangulationId,
} from "@/entities/gondola-change-groups";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { transformIntersectedProxExtra } from "@/features/gondola-change-groups/lib/helpers";
import {
  HandId,
  selectCurrentCamera,
  selectSelectedSkuSectionId,
  setHandProjections,
  setHighlightedTimelineEventId,
  setSelectedPolygon,
  setSelectedProduct,
  setSelectedSkuSectionId,
} from "@/entities/annotation";
import { useGondolasSkuSectionsQuery } from "@/shared/api";
import { getCameraSkuSections } from "@/features/sku-sections/hooks/useCameraData";
import { useTimelineData } from "@/features/annotation/hooks/useTimelineData";
import { useLazyGetHandProjectionQuery } from "@/shared/api";
import { usePlayer } from "@tezzro/player";

const VALUE_THRESHOLDS = {
  HIGH: 0.8,
  GOOD: 0.6,
  MEDIUM: 0.3,
} as const;

const COLORS = {
  TEXT_ZERO: "text-gray-400",
  TEXT_HIGH: "text-orange-600 font-semibold",
  TEXT_GOOD: "text-green-600 font-medium",
  TEXT_MEDIUM: "text-yellow-600",
  TEXT_LOW: "text-red-500",
  TEXT_WITH_ANNOTATION: "text-green-600",
  TEXT_WITHOUT_ANNOTATION: "text-gray-600",

  BG_PRIMARY_SELECTED: "#DBEAFE",
  BG_SECONDARY_SELECTED: "#FEF3C7",
  BG_EXTERNAL_SELECTED: "#E0F2FE",
  BG_ODD_ROW: "#f9fafb",
  BG_WHITE: "white",
  BG_COLUMN_HEADER: "#f3f4f6",

  BG_GRAY_100_CLASS: "bg-gray-100",
  BG_GRAY_50_CLASS: "bg-gray-50",
  BG_WHITE_CLASS: "bg-white",
  BG_HOVER_GRAY: "hover:bg-gray-100",
  BG_HOVER_BLUE: "hover:bg-blue-50",
  BG_HOVER_GRAY_200: "hover:bg-gray-200",

  BORDER_GRAY: "border-gray-200",

  SCROLLBAR_TRACK: "#f1f5f9",
  SCROLLBAR_THUMB: "#cbd5e1",
} as const;

const HIGHLIGHT_CLASSES = {
  COLUMN_HEADER: "shadow-md",
  ROW_HEADER: "shadow-sm",
  CELL: "shadow-sm",
} as const;

const STYLE_OBJECTS = {
  primarySelected: { backgroundColor: COLORS.BG_PRIMARY_SELECTED },
  secondarySelected: { backgroundColor: COLORS.BG_SECONDARY_SELECTED },
  externalSelected: { backgroundColor: COLORS.BG_EXTERNAL_SELECTED },
  columnHeaderUnselected: { backgroundColor: COLORS.BG_COLUMN_HEADER },
} as const;

interface TableRow {
  id: string;
  originalIndex: number;
  data: SKUSectionChangeGroup | IntersectedProx;
  avatar: TrackAvatarBinding | null;
}

interface TableColumn {
  id: string;
  originalIndex: number;
  data: IntersectedProx | SKUSectionChangeGroup;
  avatar: TrackAvatarBinding | null;
}

interface FilterState {
  hideZeroSSCGRows: boolean;
  hideZeroProximityColumns: boolean;
  hideZeroValues: boolean;
  isTransposed: boolean;
}

interface SelectionState {
  selectedType: "column" | "row" | "cell" | null;
  selectedId: string | null;
}

interface TableData {
  rows: TableRow[];
  columns: TableColumn[];
}

const getCellValue = (
  rowData: SKUSectionChangeGroup | IntersectedProx,
  colIndex: number,
  isTransposed: boolean,
  sscgs?: SKUSectionChangeGroup[],
  proximityLookupMap?: Map<string, number>
): number => {
  if (isTransposed) {
    const sscg = sscgs?.[colIndex];
    if (!sscg || !proximityLookupMap) return 0;

    const proxData = rowData as IntersectedProx;
    const proxKey = `${proxData.track_id}-${proxData.time_start}-${proxData.hand_id}`;
    const proxIndex = proximityLookupMap.get(proxKey) ?? -1;

    return proxIndex >= 0 ? (sscg.proxs_match_scores?.[proxIndex] ?? 0) : 0;
  } else {
    const sscgData = rowData as SKUSectionChangeGroup;
    return sscgData.proxs_match_scores?.[colIndex] ?? 0;
  }
};

const getValueColorClass = (value: number): string => {
  if (value === 0) return COLORS.TEXT_ZERO;
  if (value >= VALUE_THRESHOLDS.HIGH) return COLORS.TEXT_HIGH;
  if (value >= VALUE_THRESHOLDS.GOOD) return COLORS.TEXT_GOOD;
  if (value >= VALUE_THRESHOLDS.MEDIUM) return COLORS.TEXT_MEDIUM;
  return COLORS.TEXT_LOW;
};

const formatProximityKey = (proxData: IntersectedProx): string => {
  return `t_${proxData.track_id}_st_${proxData.time_start}_et_${proxData.time_finish}_h_${proxData.hand_id}`;
};

const getCellHighlightState = (
  rowId: string,
  colId: string,
  selectionState: SelectionState
): "primary" | "secondary" | "none" => {
  if (!selectionState.selectedType || !selectionState.selectedId) {
    return "none";
  }

  const cellId = `${rowId}-${colId}`;

  if (
    (selectionState.selectedType === "row" &&
      selectionState.selectedId === rowId) ||
    (selectionState.selectedType === "column" &&
      selectionState.selectedId === colId) ||
    (selectionState.selectedType === "cell" &&
      selectionState.selectedId === cellId)
  ) {
    return "primary";
  }

  if (selectionState.selectedType === "cell") {
    const [selectedRowId, selectedColId] = selectionState.selectedId.split("-");
    if (rowId === selectedRowId || colId === selectedColId) {
      return "secondary";
    }
  }

  return "none";
};

const getHeaderHighlightState = (
  headerId: string,
  headerType: "row" | "column",
  selectionState: SelectionState,
  externallySelectedId?: string | null
): "primary" | "secondary" | "external" | "none" => {
  if (externallySelectedId && headerId === externallySelectedId) {
    return "external";
  }

  if (!selectionState.selectedType || !selectionState.selectedId) {
    return "none";
  }

  if (
    (selectionState.selectedType === "row" &&
      headerType === "row" &&
      selectionState.selectedId === headerId) ||
    (selectionState.selectedType === "column" &&
      headerType === "column" &&
      selectionState.selectedId === headerId)
  ) {
    return "primary";
  }

  if (selectionState.selectedType === "cell") {
    const [selectedRowId, selectedColId] = selectionState.selectedId.split("-");
    if (
      (headerType === "row" && headerId === selectedRowId) ||
      (headerType === "column" && headerId === selectedColId)
    ) {
      return "secondary";
    }
  }

  return "none";
};

const useCellValuesCache = (
  tableData: TableData,
  filters: FilterState,
  sscgs: SKUSectionChangeGroup[],
  intersectedProxs: IntersectedProx[]
) => {
  return useMemo(() => {
    const cache = new Map<string, number>();
    const proximityLookupMap = new Map<string, number>();

    intersectedProxs.forEach((prox, index) => {
      const key = `${prox.track_id}-${prox.time_start}-${prox.hand_id}`;
      proximityLookupMap.set(key, index);
    });

    tableData.rows.forEach((row) => {
      tableData.columns.forEach((col) => {
        const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
        const value = getCellValue(
          row.data,
          col.originalIndex,
          filters.isTransposed,
          sscgs,
          proximityLookupMap
        );
        cache.set(cacheKey, value);
      });
    });

    return cache;
  }, [tableData, filters.isTransposed, sscgs, intersectedProxs]);
};

const SkuImage = memo(
  ({
    src,
    alt,
    fallbackText,
  }: {
    src: string;
    alt: string;
    fallbackText: string;
  }) => {
    const [imageError, setImageError] = useState(false);

    if (imageError) {
      return <span>{fallbackText}</span>;
    }

    return (
      <img
        src={src}
        alt={alt}
        className="w-20 h-20 object-contain"
        loading="lazy"
        onError={() => setImageError(true)}
      />
    );
  }
);

SkuImage.displayName = "SkuImage";

const CopyButton = ({
  proxData,
  copiedStates,
  onCopy,
}: {
  proxData: IntersectedProx;
  copiedStates: Record<string, boolean>;
  onCopy: (proxData: IntersectedProx) => void;
}) => {
  const copyKey = formatProximityKey(proxData);
  const isCopied = copiedStates[copyKey];

  return (
    <button
      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded z-10"
      onClick={(e) => {
        e.stopPropagation();
        onCopy(proxData);
      }}
      aria-label="Copy proximity data"
    >
      {isCopied ? (
        <CheckIcon className="w-4 h-4 text-green-600" />
      ) : (
        <CopyIcon className="w-4 h-4 text-gray-600" />
      )}
    </button>
  );
};

const TableRow = memo(
  ({
    rowIndex,
    row,
    visibleColumns,
    filters,
    selectionState,
    handleRowHeaderClick,
    handleCellClick,
    cellValuesCache,
    getRowSkuSectionImage,
    externallySelectedTableId,
    copiedStates,
    handleProximityCopy,
  }: {
    rowIndex: number;
    row: TableRow;
    visibleColumns: TableColumn[];
    filters: FilterState;
    selectionState: SelectionState;
    handleRowHeaderClick: (row: TableRow) => void;
    handleCellClick: (row: TableRow, col: TableColumn, value: number) => void;
    cellValuesCache: Map<string, number>;
    getRowSkuSectionImage: (
      rowData: SKUSectionChangeGroup | IntersectedProx
    ) => string | undefined;
    externallySelectedTableId: string | null;
    copiedStates: Record<string, boolean>;
    handleProximityCopy: (proxData: IntersectedProx) => void;
  }) => {
    const rowHeaderHighlightState = getHeaderHighlightState(
      row.id,
      "row",
      selectionState,
      externallySelectedTableId
    );

    const isProximityRow = filters.isTransposed;

    const rowHeaderStyle = useMemo(() => {
      if (rowHeaderHighlightState === "primary") {
        return STYLE_OBJECTS.primarySelected;
      }
      if (rowHeaderHighlightState === "secondary") {
        return STYLE_OBJECTS.secondarySelected;
      }
      if (rowHeaderHighlightState === "external") {
        return STYLE_OBJECTS.externalSelected;
      }
      return {
        backgroundColor:
          rowIndex % 2 === 0 ? COLORS.BG_WHITE : COLORS.BG_ODD_ROW,
      };
    }, [rowHeaderHighlightState, rowIndex]);

    const rowCellData = useMemo(() => {
      return visibleColumns.map((col) => {
        const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
        const value = cellValuesCache.get(cacheKey) ?? 0;

        const shouldShowValue = !filters.hideZeroValues || value !== 0;
        const cellId = `${row.id}-${col.id}`;
        const highlightState = getCellHighlightState(
          row.id,
          col.id,
          selectionState
        );

        let cellStyle;
        if (highlightState === "primary") {
          cellStyle = STYLE_OBJECTS.primarySelected;
        } else if (highlightState === "secondary") {
          cellStyle = STYLE_OBJECTS.secondarySelected;
        } else {
          cellStyle = undefined;
        }

        return {
          col,
          value,
          shouldShowValue,
          cellId,
          highlightState,
          cellStyle,
          colorClass: getValueColorClass(value),
          formattedValue: (Math.trunc(value * 100) / 100).toFixed(2),
        };
      });
    }, [
      row.id,
      visibleColumns,
      filters.hideZeroValues,
      filters.isTransposed,
      selectionState,
      cellValuesCache,
    ]);

    return (
      <tr
        className={twMerge(
          rowIndex % 2 === 0 ? COLORS.BG_WHITE_CLASS : COLORS.BG_GRAY_50_CLASS,
          "border-b border-gray-200"
        )}
        role="row"
      >
        <th
          className={twMerge(
            `sticky left-0 z-10  text-sm font-medium text-gray-900 cursor-pointer transition-colors group`,
            rowHeaderHighlightState === "primary"
              ? HIGHLIGHT_CLASSES.ROW_HEADER
              : COLORS.BG_HOVER_GRAY
          )}
          style={{
            ...rowHeaderStyle,
            minWidth: getRowSkuSectionImage(row.data) ? "100px" : "60px",
            width: getRowSkuSectionImage(row.data) ? "100px" : "60px",
            borderRight: "1px solid #e5e7eb",
            padding: "8px",
          }}
          onClick={() => handleRowHeaderClick(row)}
          scope="row"
          role="rowheader"
          tabIndex={0}
          data-row-id={row.id}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleRowHeaderClick(row);
            }
          }}
          aria-label={`Row ${row.id}${rowHeaderHighlightState === "primary" ? " (selected)" : ""}`}
        >
          <div className="flex flex-col items-center gap-1 min-w-0">
            <span
              className={
                filters.isTransposed
                  ? COLORS.TEXT_WITHOUT_ANNOTATION
                  : (row.data as SKUSectionChangeGroup).has_annotation
                    ? COLORS.TEXT_WITH_ANNOTATION
                    : COLORS.TEXT_WITHOUT_ANNOTATION
              }
            >
              {row.id}
            </span>
            {isProximityRow && (
              <CopyButton
                proxData={row.data as IntersectedProx}
                copiedStates={copiedStates}
                onCopy={handleProximityCopy}
              />
            )}
            {(() => {
              const skuImage = getRowSkuSectionImage(row.data);
              if (skuImage) {
                return (
                  <div className="flex-shrink-0">
                    <SkuImage
                      src={skuImage}
                      alt={`SKU ${row.id}`}
                      fallbackText=""
                    />
                  </div>
                );
              }
              return null;
            })()}
            {row.avatar && (
              <Animal
                name={row.avatar.animalName}
                color={row.avatar.color}
                size="24px"
              />
            )}
          </div>
        </th>
        {rowCellData.map((cellData) => (
          <td
            key={cellData.cellId}
            className={twMerge(
              `text-sm text-center ${COLORS.BORDER_GRAY} border-r cursor-pointer transition-colors`,
              cellData.highlightState === "primary"
                ? HIGHLIGHT_CLASSES.CELL
                : COLORS.BG_HOVER_BLUE
            )}
            style={{
              ...cellData.cellStyle,
              minWidth: "60px",
              width: "60px",
              padding: "8px",
            }}
            onClick={() => handleCellClick(row, cellData.col, cellData.value)}
            role="gridcell"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCellClick(row, cellData.col, cellData.value);
              }
            }}
            aria-label={`Cell ${cellData.cellId}: ${
              cellData.shouldShowValue ? cellData.formattedValue : "hidden"
            }${cellData.highlightState === "primary" ? " (selected)" : ""}`}
          >
            {cellData.shouldShowValue && (
              <span className={cellData.colorClass}>
                {cellData.formattedValue}
              </span>
            )}
          </td>
        ))}
      </tr>
    );
  }
);

TableRow.displayName = "TableRow";

interface SkuSectionChangeGroupTableProps {
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

export function SkuSectionChangeGroupTable({
  isExpanded = false,
  onToggleExpanded,
}: SkuSectionChangeGroupTableProps) {
  const dispatch = useAppDispatch();
  const player = usePlayer();
  const currentCamera = useAppSelector(selectCurrentCamera);
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const selectedSkuSectionId = useAppSelector(selectSelectedSkuSectionId);

  const {
    selectedGondolaChangeGroup,
    skuSectionChangeGroupsForSelectedGondolaChange,
    findFirstSkuSectionChangeWithDetection,
    handleSkuSectionProxsHighlighting,
    selectedGondolaChange,
    highlightSingleProx,
    findTrackAvatar,
  } = useGondolaChangeGroupsData();
  const {
    highlightIntersectedProxsByMatchScores,
    clearIntersectedProxsHighlighting,
    addSelectedProximityToTimeline,
    clearSelectedProximityFromTimeline,
  } = useTimelineData();

  const { data: skuSectionsData } = useGondolasSkuSectionsQuery(
    {
      gondolaId: String(selectedGondolaChangeGroup?.gondola_id),
    },
    { skip: !selectedGondolaChangeGroup || !selectedOrder }
  );

  const [getHandProjection] = useLazyGetHandProjectionQuery();

  const fetchHandProjection = useCallback(
    async (proxData: IntersectedProx, currentCamera: string) => {
      dispatch(setHandProjections(null));
      try {
        const result = await getHandProjection({
          track_id: proxData.track_id,
          from_datetime: proxData.time_start,
          to_datetime: proxData.time_finish,
          camera_id: currentCamera || "",
          hand_id: proxData.hand_id,
        });

        if (result.data) {
          const { results, count } = result.data;
          const gondolaDistances = proxData.wrist_gondola_signed_distances;

          let projections = results;

          if (gondolaDistances) {
            if (gondolaDistances.length === count) {
              projections = results.map((projection, index) => ({
                ...projection,
                gondola_distance: gondolaDistances[index],
              }));
            } else {
              toast.error(
                `Gondola distances count (${gondolaDistances.length}) doesn't match projections count (${count})`
              );
            }
          }

          dispatch(
            setHandProjections({
              projections,
              handId: String(proxData.hand_id),
              trackId: proxData.track_id,
            })
          );
        }
      } catch (error) {
        console.error("❌ Hand projection error:", error);
      }
    },
    [getHandProjection, dispatch]
  );

  const baseCameraSkuSections = skuSectionsData?.results
    ? getCameraSkuSections(skuSectionsData, currentCamera)
    : null;

  const [filters, setFilters] = useState<FilterState>({
    hideZeroSSCGRows: true,
    hideZeroProximityColumns: true,
    hideZeroValues: false,
    isTransposed: true,
  });

  const [selectionState, setSelectionState] = useState<SelectionState>({
    selectedType: null,
    selectedId: null,
  });

  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInternalSelectionRef = useRef(false);
  const prevSelectedSkuSectionIdRef = useRef(selectedSkuSectionId);

  const blurActiveElement = useCallback(() => {
    const BLUR_DELAY_MS = 10;

    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (tableContainerRef.current?.contains(activeElement)) {
        activeElement?.blur?.();
      }
    }, BLUR_DELAY_MS);
  }, []);

  const handleProximityCopy = useCallback(async (proxData: IntersectedProx) => {
    const formattedText = formatProximityKey(proxData);

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopiedStates((prev) => ({ ...prev, [formattedText]: true }));

      setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [formattedText]: false }));
      }, 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  }, []);

  useTrackedHotkeys(
    "up,down,left,right",
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isTableFocused =
        activeElement && tableContainerRef.current?.contains(activeElement);

      if (isTableFocused || activeElement === document.body) {
        e.preventDefault();
      }
    },
    { preventDefault: false }
  );

  // Reset selection state when selectedGondolaChange changes to prevent
  // stale row/column header and cell selections from persisting
  useEffect(() => {
    setSelectionState({
      selectedType: null,
      selectedId: null,
    });
    dispatch(setSelectedSkuSectionChangeGroupTableData(null));
    clearIntersectedProxsHighlighting();
  }, [selectedGondolaChange, dispatch, clearIntersectedProxsHighlighting]);

  const handleProximitySelection = useCallback(
    (proxData: IntersectedProx) => {
      const avatarBinding = findTrackAvatar(proxData.track_id);

      addSelectedProximityToTimeline(
        proxData.track_id,
        proxData.extra,
        avatarBinding
          ? {
              animalName: avatarBinding.animalName,
              color: avatarBinding.color,
            }
          : undefined
      );

      const transformedItems = transformIntersectedProxExtra(
        proxData.track_id,
        String(proxData.hand_id) as HandId,
        proxData.extra
      );

      dispatch(setHighlightedTimelineEventId(null));
      dispatch(setSelectedClosestGondolaDistItem(null));
      dispatch(setClosestGondolaDistItems(transformedItems));
    },
    [dispatch, addSelectedProximityToTimeline, findTrackAvatar]
  );

  const intersectedProxs = useMemo(
    () => selectedGondolaChangeGroup?.intersected_proxs ?? [],
    [selectedGondolaChangeGroup?.intersected_proxs]
  );
  const sscgs = useMemo(
    () => skuSectionChangeGroupsForSelectedGondolaChange ?? [],
    [skuSectionChangeGroupsForSelectedGondolaChange]
  );

  const tableData = useMemo((): TableData => {
    if (intersectedProxs.length === 0 || sscgs.length === 0) {
      return { rows: [], columns: [] };
    }

    if (filters.isTransposed) {
      const rows: TableRow[] = intersectedProxs.map((prox, index) => ({
        id: `p${index + 1}`,
        originalIndex: index,
        data: prox,
        avatar: findTrackAvatar(prox.track_id),
      }));

      const columns: TableColumn[] = sscgs.map((sscg, index) => ({
        id: `sscg${index + 1}`,
        originalIndex: index,
        data: sscg,
        avatar: null,
      }));

      return { rows, columns };
    } else {
      const rows: TableRow[] = sscgs.map((sscg, index) => ({
        id: `sscg${index + 1}`,
        originalIndex: index,
        data: sscg,
        avatar: null,
      }));

      const columns: TableColumn[] = intersectedProxs.map((prox, index) => ({
        id: `p${index + 1}`,
        originalIndex: index,
        data: prox,
        avatar: findTrackAvatar(prox.track_id),
      }));

      return { rows, columns };
    }
  }, [intersectedProxs, sscgs, findTrackAvatar, filters.isTransposed]);

  const cellValuesCache = useCellValuesCache(
    tableData,
    filters,
    sscgs,
    intersectedProxs
  );

  const externallySelectedTableId = useMemo(() => {
    if (!selectedSkuSectionId) return null;

    const matchingSscg = sscgs.find(
      (sscg) => sscg.sku_section_id === selectedSkuSectionId
    );
    if (!matchingSscg) return null;

    const sscgIndex = sscgs.indexOf(matchingSscg);

    if (filters.isTransposed) {
      return `sscg${sscgIndex + 1}`;
    } else {
      return `sscg${sscgIndex + 1}`;
    }
  }, [selectedSkuSectionId, sscgs, filters.isTransposed]);

  useEffect(() => {
    const hasSelectedSkuSectionIdChanged =
      prevSelectedSkuSectionIdRef.current !== selectedSkuSectionId;

    prevSelectedSkuSectionIdRef.current = selectedSkuSectionId;

    if (
      hasSelectedSkuSectionIdChanged &&
      selectedSkuSectionId &&
      externallySelectedTableId
    ) {
      const currentInternalSelection = selectionState.selectedId;

      let shouldClear = true;

      if (currentInternalSelection) {
        if (
          selectionState.selectedType === "row" ||
          selectionState.selectedType === "column"
        ) {
          shouldClear = currentInternalSelection !== externallySelectedTableId;
        } else if (selectionState.selectedType === "cell") {
          const [selectedRowId, selectedColId] =
            currentInternalSelection.split("-");
          const matchesRow = selectedRowId === externallySelectedTableId;
          const matchesCol = selectedColId === externallySelectedTableId;
          shouldClear = !matchesRow && !matchesCol;
        }
      }

      if (shouldClear) {
        setSelectionState({
          selectedType: null,
          selectedId: null,
        });

        dispatch(setSelectedSkuSectionChangeGroupTableData(null));

        clearIntersectedProxsHighlighting();
      }
    }
  }, [
    selectedSkuSectionId,
    externallySelectedTableId,
    selectionState.selectedId,
    selectionState.selectedType,
    dispatch,
    clearIntersectedProxsHighlighting,
    clearSelectedProximityFromTimeline,
  ]);

  useEffect(() => {
    if (!externallySelectedTableId || !tableContainerRef.current) return;

    if (isInternalSelectionRef.current) {
      isInternalSelectionRef.current = false;
      return;
    }

    const scrollToElement = () => {
      const targetSelector = filters.isTransposed
        ? `th[data-column-id="${externallySelectedTableId}"]`
        : `th[data-row-id="${externallySelectedTableId}"]`;

      const targetElement =
        tableContainerRef.current?.querySelector(targetSelector);

      if (targetElement) {
        if (filters.isTransposed) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        } else {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }
    };

    const timeoutId = setTimeout(scrollToElement, 100);
    return () => clearTimeout(timeoutId);
  }, [externallySelectedTableId, filters.isTransposed]);

  const { visibleRows, visibleColumns } = useMemo(() => {
    const hideZeroRowsFilter = filters.isTransposed
      ? filters.hideZeroProximityColumns
      : filters.hideZeroSSCGRows;
    const hideZeroColumnsFilter = filters.isTransposed
      ? filters.hideZeroSSCGRows
      : filters.hideZeroProximityColumns;

    if (!hideZeroRowsFilter && !hideZeroColumnsFilter) {
      return {
        visibleRows: tableData.rows,
        visibleColumns: tableData.columns,
      };
    }

    let filteredRows = tableData.rows;
    let filteredColumns = tableData.columns;

    if (hideZeroRowsFilter) {
      filteredRows = filteredRows.filter((row) => {
        for (const col of filteredColumns) {
          const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
          const value = cellValuesCache.get(cacheKey) ?? 0;
          if (value !== 0) {
            return true;
          }
        }
        return false;
      });
    }

    if (hideZeroColumnsFilter) {
      filteredColumns = filteredColumns.filter((col) => {
        for (const row of filteredRows) {
          const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
          const value = cellValuesCache.get(cacheKey) ?? 0;
          if (value !== 0) {
            return true;
          }
        }
        return false;
      });
    }

    return {
      visibleRows: filteredRows,
      visibleColumns: filteredColumns,
    };
  }, [
    tableData,
    filters.hideZeroSSCGRows,
    filters.hideZeroProximityColumns,
    filters.isTransposed,
    cellValuesCache,
  ]);

  const handleFilterChange = useCallback(
    (filterKey: keyof FilterState) => {
      return (checked: boolean | "indeterminate") => {
        setFilters((prev) => ({
          ...prev,
          [filterKey]: Boolean(checked),
        }));
        blurActiveElement();
      };
    },
    [blurActiveElement]
  );

  const handleRowHeaderClick = useCallback(
    (row: TableRow) => {
      isInternalSelectionRef.current = true;
      blurActiveElement();
      dispatch(setHandProjections(null));

      const isCurrentlySelected =
        selectionState.selectedType === "row" &&
        selectionState.selectedId === row.id;

      if (filters.isTransposed) {
        const proxData = row.data as IntersectedProx;

        if (isCurrentlySelected) {
          dispatch(setSelectedSkuSectionChangeGroupTableData(null));
          dispatch(clearClosestGondolaDistItems());
          clearSelectedProximityFromTimeline();
        } else {
          player.seek(proxData.time_start, currentCamera);

          const sscgsData: SKUSectionChangeGroup[] = [];
          for (const col of visibleColumns) {
            const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
            const cellValue = cellValuesCache.get(cacheKey) ?? 0;
            if (cellValue > 0) {
              sscgsData.push(col.data as SKUSectionChangeGroup);
            }
          }

          dispatch(setSelectedSkuSectionChangeGroupTableData(sscgsData));
          handleProximitySelection(proxData);
        }

        setSelectionState((prev) => {
          if (prev.selectedType === "row" && prev.selectedId === row.id) {
            return { selectedType: null, selectedId: null };
          } else {
            return { selectedType: "row", selectedId: row.id };
          }
        });

        highlightSingleProx(
          proxData.track_id,
          proxData.time_start,
          proxData.hand_id,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );
      } else {
        const sscgData = row.data as SKUSectionChangeGroup;

        const occlusionStartTime = sscgData.sku_section_changes.find(
          (change) =>
            change.gondola_change_camera_composite_id ===
            selectedGondolaChange?.gondola_change_camera_composite_id
        )?.occlusion_start_time;

        if (occlusionStartTime) {
          player.seek(occlusionStartTime, currentCamera);
        }
        setSelectionState((prev) => {
          if (prev.selectedType === "row" && prev.selectedId === row.id) {
            return { selectedType: null, selectedId: null };
          } else {
            return { selectedType: "row", selectedId: row.id };
          }
        });

        const skuSectionId = sscgData?.sku_section_id;
        const firstChangeWithDetection =
          findFirstSkuSectionChangeWithDetection(skuSectionId);
        const polygon = baseCameraSkuSections?.find(
          (sku) => sku.sku_section_id === skuSectionId
        );

        if (firstChangeWithDetection) {
          dispatch(
            setSelectedSkuSectionChangeId(
              firstChangeWithDetection?.gondola_change_camera_composite_id
            )
          );
          dispatch(
            setSelectedDetectionId(
              firstChangeWithDetection?.detection?.id?.toString() ||
                firstChangeWithDetection?.triangulation?.triangulation_id ||
                null
            )
          );
          dispatch(
            setSelectedTriangulationId(
              firstChangeWithDetection?.triangulation?.triangulation_id || null
            )
          );
        }

        if (isCurrentlySelected) {
          dispatch(setSelectedSkuSectionId(null));
          dispatch(setSelectedPolygon(null));
          dispatch(setSelectedProduct(null));
          handleSkuSectionProxsHighlighting(
            null,
            highlightIntersectedProxsByMatchScores,
            clearIntersectedProxsHighlighting
          );
          return;
        }

        handleSkuSectionProxsHighlighting(
          skuSectionId,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );
        dispatch(setSelectedPolygon(polygon || null));
        dispatch(setSelectedSkuSectionId(skuSectionId));
        if (polygon) {
          dispatch(
            setSelectedProduct({
              name: polygon.variant.name,
              imageUrl: polygon.variant.image,
              barcode: polygon.variant.barcode,
              skuId: polygon.variant.id,
              variantId: polygon.variant.variant_id,
              count: 1,
              unitOfMeasurement: polygon.variant.uom,
              unitOfMeasurementInternal: polygon.variant.uom_internal,
            })
          );
        }

        dispatch(clearClosestGondolaDistItems());
        clearSelectedProximityFromTimeline();
      }
    },
    [
      filters.isTransposed,
      visibleColumns,
      cellValuesCache,
      baseCameraSkuSections,
      findFirstSkuSectionChangeWithDetection,
      clearIntersectedProxsHighlighting,
      highlightIntersectedProxsByMatchScores,
      handleSkuSectionProxsHighlighting,
      highlightSingleProx,
      currentCamera,
      selectedGondolaChange,
      selectionState,
      player,
      dispatch,
      blurActiveElement,
      clearSelectedProximityFromTimeline,
      handleProximitySelection,
    ]
  );

  const handleColumnHeaderClick = useCallback(
    (col: TableColumn) => {
      isInternalSelectionRef.current = true;
      blurActiveElement();
      dispatch(setHandProjections(null));

      const isCurrentlySelected =
        selectionState.selectedType === "column" &&
        selectionState.selectedId === col.id;

      if (filters.isTransposed) {
        const sscgData = col.data as SKUSectionChangeGroup;

        const occlusionStartTime = sscgData.sku_section_changes.find(
          (change) =>
            change.gondola_change_camera_composite_id ===
            selectedGondolaChange?.gondola_change_camera_composite_id
        )?.occlusion_start_time;

        if (occlusionStartTime) {
          player.seek(occlusionStartTime, currentCamera);
        }

        if (isCurrentlySelected) {
          dispatch(setSelectedSkuSectionChangeGroupTableData(null));
          dispatch(setSelectedSkuSectionId(null));
          dispatch(setSelectedPolygon(null));
          dispatch(setSelectedProduct(null));
          handleSkuSectionProxsHighlighting(
            null,
            highlightIntersectedProxsByMatchScores,
            clearIntersectedProxsHighlighting
          );
        } else {
          const sscgsData: SKUSectionChangeGroup[] = [];
          for (const row of visibleRows) {
            const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
            const cellValue = cellValuesCache.get(cacheKey) ?? 0;
            if (cellValue > 0) {
              sscgsData.push(sscgData);
            }
          }

          dispatch(setSelectedSkuSectionChangeGroupTableData(sscgsData));

          const skuSectionId = sscgData?.sku_section_id;
          const firstChangeWithDetection =
            findFirstSkuSectionChangeWithDetection(skuSectionId);
          const polygon = baseCameraSkuSections?.find(
            (sku) => sku.sku_section_id === skuSectionId
          );

          if (firstChangeWithDetection) {
            dispatch(
              setSelectedSkuSectionChangeId(
                firstChangeWithDetection?.gondola_change_camera_composite_id
              )
            );
          }

          handleSkuSectionProxsHighlighting(
            skuSectionId,
            highlightIntersectedProxsByMatchScores,
            clearIntersectedProxsHighlighting
          );
          dispatch(setSelectedPolygon(polygon || null));
          dispatch(setSelectedSkuSectionId(skuSectionId));
          if (polygon) {
            dispatch(
              setSelectedProduct({
                name: polygon.variant.name,
                imageUrl: polygon.variant.image,
                barcode: polygon.variant.barcode,
                skuId: polygon.variant.id,
                variantId: polygon.variant.variant_id,
                count: 1,
                unitOfMeasurement: polygon.variant.uom,
                unitOfMeasurementInternal: polygon.variant.uom_internal,
              })
            );
          }
        }

        dispatch(clearClosestGondolaDistItems());
        clearSelectedProximityFromTimeline();
      } else {
        const proxData = col.data as IntersectedProx;

        if (isCurrentlySelected) {
          dispatch(setSelectedSkuSectionChangeGroupTableData(null));
          dispatch(clearClosestGondolaDistItems());
          clearSelectedProximityFromTimeline();
        } else {
          player.seek(proxData.time_start, currentCamera);

          const sscgsData: SKUSectionChangeGroup[] = [];
          for (const row of visibleRows) {
            const cacheKey = `${row.id}-${col.id}-${filters.isTransposed}`;
            const cellValue = cellValuesCache.get(cacheKey) ?? 0;
            if (cellValue > 0) {
              sscgsData.push(row.data as SKUSectionChangeGroup);
            }
          }

          dispatch(setSelectedSkuSectionChangeGroupTableData(sscgsData));
          handleProximitySelection(proxData);
        }

        highlightSingleProx(
          proxData.track_id,
          proxData.time_start,
          proxData.hand_id,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );
      }

      setSelectionState((prev) => {
        const newState =
          prev.selectedType === "column" && prev.selectedId === col.id
            ? { selectedType: null, selectedId: null }
            : { selectedType: "column" as const, selectedId: col.id };
        return newState;
      });
    },
    [
      filters.isTransposed,
      visibleRows,
      cellValuesCache,
      baseCameraSkuSections,
      findFirstSkuSectionChangeWithDetection,
      clearIntersectedProxsHighlighting,
      highlightIntersectedProxsByMatchScores,
      handleSkuSectionProxsHighlighting,
      highlightSingleProx,
      selectionState,
      selectedGondolaChange,
      currentCamera,
      player,
      dispatch,
      blurActiveElement,
      clearSelectedProximityFromTimeline,
      handleProximitySelection,
    ]
  );

  const handleCellClick = useCallback(
    (row: TableRow, col: TableColumn, _value: number) => {
      isInternalSelectionRef.current = true;
      blurActiveElement();
      dispatch(setHandProjections(null));

      const cellId = `${row.id}-${col.id}`;
      const isCurrentlySelected =
        selectionState.selectedType === "cell" &&
        selectionState.selectedId === cellId;

      setSelectionState((prev) => {
        if (prev.selectedType === "cell" && prev.selectedId === cellId) {
          return { selectedType: null, selectedId: null };
        } else {
          return { selectedType: "cell", selectedId: cellId };
        }
      });

      if (isCurrentlySelected) {
        dispatch(setSelectedSkuSectionChangeGroupTableData(null));
        dispatch(setSelectedSkuSectionId(null));
        dispatch(setSelectedPolygon(null));
        dispatch(setSelectedProduct(null));
        handleSkuSectionProxsHighlighting(
          null,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );
        dispatch(clearClosestGondolaDistItems());
        clearSelectedProximityFromTimeline();
        return;
      }

      if (filters.isTransposed) {
        const proxData = row.data as IntersectedProx;
        const sscgData = col.data as SKUSectionChangeGroup;

        fetchHandProjection(proxData, currentCamera || "");

        const skuSectionId = sscgData?.sku_section_id;
        const firstChangeWithDetection =
          findFirstSkuSectionChangeWithDetection(skuSectionId);
        const polygon = baseCameraSkuSections?.find(
          (sku) => sku.sku_section_id === skuSectionId
        );

        if (firstChangeWithDetection) {
          dispatch(
            setSelectedSkuSectionChangeId(
              firstChangeWithDetection?.gondola_change_camera_composite_id
            )
          );
        }

        player.seek(proxData.time_start, currentCamera);

        handleSkuSectionProxsHighlighting(
          skuSectionId,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );
        dispatch(setSelectedPolygon(polygon || null));
        dispatch(setSelectedSkuSectionId(skuSectionId));
        if (polygon) {
          dispatch(
            setSelectedProduct({
              name: polygon.variant.name,
              imageUrl: polygon.variant.image,
              barcode: polygon.variant.barcode,
              skuId: polygon.variant.id,
              variantId: polygon.variant.variant_id,
              count: 1,
              unitOfMeasurement: polygon.variant.uom,
              unitOfMeasurementInternal: polygon.variant.uom_internal,
            })
          );
        }

        highlightSingleProx(
          proxData.track_id,
          proxData.time_start,
          proxData.hand_id,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );

        handleProximitySelection(proxData);
      } else {
        const sscgData = row.data as SKUSectionChangeGroup;
        const proxData = col.data as IntersectedProx;

        fetchHandProjection(proxData, currentCamera || "");

        const skuSectionId = sscgData?.sku_section_id;
        const firstChangeWithDetection =
          findFirstSkuSectionChangeWithDetection(skuSectionId);
        const polygon = baseCameraSkuSections?.find(
          (sku) => sku.sku_section_id === skuSectionId
        );

        if (firstChangeWithDetection) {
          dispatch(
            setSelectedSkuSectionChangeId(
              firstChangeWithDetection?.gondola_change_camera_composite_id
            )
          );
        }

        player.seek(proxData.time_start, currentCamera);

        handleSkuSectionProxsHighlighting(
          skuSectionId,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );
        dispatch(setSelectedPolygon(polygon || null));
        dispatch(setSelectedSkuSectionId(skuSectionId));
        if (polygon) {
          dispatch(
            setSelectedProduct({
              name: polygon.variant.name,
              imageUrl: polygon.variant.image,
              barcode: polygon.variant.barcode,
              skuId: polygon.variant.id,
              variantId: polygon.variant.variant_id,
              count: 1,
              unitOfMeasurement: polygon.variant.uom,
              unitOfMeasurementInternal: polygon.variant.uom_internal,
            })
          );
        }

        highlightSingleProx(
          proxData.track_id,
          proxData.time_start,
          proxData.hand_id,
          highlightIntersectedProxsByMatchScores,
          clearIntersectedProxsHighlighting
        );

        handleProximitySelection(proxData);
      }
    },
    [
      filters.isTransposed,
      baseCameraSkuSections,
      findFirstSkuSectionChangeWithDetection,
      clearIntersectedProxsHighlighting,
      highlightIntersectedProxsByMatchScores,
      handleSkuSectionProxsHighlighting,
      selectionState,
      highlightSingleProx,
      currentCamera,
      player,
      dispatch,
      blurActiveElement,
      clearSelectedProximityFromTimeline,
      handleProximitySelection,
      fetchHandProjection,
    ]
  );

  const skuSectionLookupMap = useMemo(() => {
    if (!baseCameraSkuSections) return new Map();

    const map = new Map<number, string>();
    baseCameraSkuSections.forEach((sku) => {
      map.set(sku.variant.id, sku.variant.image);
    });
    return map;
  }, [baseCameraSkuSections]);

  const getSkuSectionImage = useCallback(
    (colData: SKUSectionChangeGroup | IntersectedProx): string | undefined => {
      if (!filters.isTransposed) return undefined;

      const sscgData = colData as SKUSectionChangeGroup;
      return skuSectionLookupMap.get(sscgData.sku_base_id);
    },
    [skuSectionLookupMap, filters.isTransposed]
  );

  const getRowSkuSectionImage = useCallback(
    (rowData: SKUSectionChangeGroup | IntersectedProx): string | undefined => {
      if (filters.isTransposed) return undefined;

      const sscgData = rowData as SKUSectionChangeGroup;
      return skuSectionLookupMap.get(sscgData.sku_base_id);
    },
    [skuSectionLookupMap, filters.isTransposed]
  );

  const memoizedColumnHeaders = useMemo(
    () =>
      visibleColumns.map((col) => {
        const columnHeaderHighlightState = getHeaderHighlightState(
          col.id,
          "column",
          selectionState,
          externallySelectedTableId
        );

        const skuImage = getSkuSectionImage(col.data);
        const ariaLabel = `Column ${col.id}${col.avatar ? ` - ${col.avatar.animalName}` : ""}${columnHeaderHighlightState === "primary" ? " (selected)" : ""}`;

        let columnHeaderStyle;
        if (columnHeaderHighlightState === "primary") {
          columnHeaderStyle = STYLE_OBJECTS.primarySelected;
        } else if (columnHeaderHighlightState === "secondary") {
          columnHeaderStyle = STYLE_OBJECTS.secondarySelected;
        } else if (columnHeaderHighlightState === "external") {
          columnHeaderStyle = STYLE_OBJECTS.externalSelected;
        } else {
          columnHeaderStyle = STYLE_OBJECTS.columnHeaderUnselected;
        }

        const isProximityColumn = !filters.isTransposed;
        const proxData = isProximityColumn
          ? (col.data as IntersectedProx)
          : null;

        const getColumnWidth = () => {
          if (skuImage) return "100px"; // SKU images need more space
          if (isProximityColumn) return "80px"; // proximity columns need space for copy icon
          return "60px"; // default width
        };

        return (
          <th
            key={col.id}
            className={`text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer transition-colors relative group ${
              columnHeaderHighlightState === "primary"
                ? `${HIGHLIGHT_CLASSES.COLUMN_HEADER}`
                : `${COLORS.BG_HOVER_GRAY_200}`
            }`}
            style={{
              ...columnHeaderStyle,
              minWidth: getColumnWidth(),
              width: getColumnWidth(),
              borderRight: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              padding: "8px",
            }}
            onClick={() => handleColumnHeaderClick(col)}
            scope="col"
            role="columnheader"
            tabIndex={0}
            data-column-id={col.id}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleColumnHeaderClick(col);
              }
            }}
            aria-label={ariaLabel}
          >
            <div
              className={`flex flex-col items-center gap-1 min-w-0 ${col.avatar ? "px-3" : ""}`}
            >
              <span>{col.id}</span>
              {isProximityColumn && proxData && (
                <CopyButton
                  proxData={proxData}
                  copiedStates={copiedStates}
                  onCopy={handleProximityCopy}
                />
              )}
              {skuImage && (
                <div className="flex-shrink-0">
                  <SkuImage
                    src={skuImage}
                    alt={`SKU ${col.id}`}
                    fallbackText=""
                  />
                </div>
              )}
              {col.avatar && (
                <Animal
                  name={col.avatar.animalName}
                  color={col.avatar.color}
                  size="24px"
                />
              )}
            </div>
          </th>
        );
      }),
    [
      visibleColumns,
      selectionState,
      handleColumnHeaderClick,
      getSkuSectionImage,
      externallySelectedTableId,
      filters.isTransposed,
      copiedStates,
      handleProximityCopy,
    ]
  );

  return (
    <>
      <Separator orientation="horizontal" size="4" />
      <Flex
        direction="column"
        gap="2"
        flexShrink="1"
        flexGrow={isExpanded ? "1" : "0"}
        className={`overflow-hidden ${isExpanded ? "h-auto" : "h-10"}`}
      >
        <section
          className={isExpanded ? "p-2" : "p-3"}
          aria-label="Table filters"
        >
          <Flex
            direction="row"
            justify="between"
            align="center"
            className={`${isExpanded ? "mb-2" : "mb-3"} ${
              onToggleExpanded
                ? `cursor-pointer ${COLORS.BG_HOVER_GRAY} rounded-md px-2 py-1 -mx-2 -my-1 transition-colors`
                : ""
            }`}
            onClick={(e) => {
              if (onToggleExpanded === undefined) {
                return;
              }

              onToggleExpanded();
              (e.currentTarget as HTMLElement).blur();
            }}
            role={onToggleExpanded ? "button" : undefined}
            tabIndex={onToggleExpanded ? 0 : undefined}
            onKeyDown={
              onToggleExpanded
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onToggleExpanded();
                    }
                  }
                : undefined
            }
            aria-label={
              onToggleExpanded
                ? isExpanded
                  ? "Collapse table"
                  : "Expand table"
                : undefined
            }
          >
            <Text size="2" weight="bold">
              SSCG Proximity Affinity Matrix
            </Text>
            {onToggleExpanded && (
              <div className="pointer-events-none">
                {isExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
              </div>
            )}
          </Flex>

          <Flex
            direction="column"
            gap="2"
            justify="center"
            className="flex-wrap"
          >
            <Flex gap="2" align="center">
              <Checkbox
                checked={filters.hideZeroSSCGRows}
                onCheckedChange={handleFilterChange("hideZeroSSCGRows")}
                id="hide-zero-sscg-rows"
              />
              <label htmlFor="hide-zero-sscg-rows">
                <Text size="2">Hide zero SSCGs</Text>
              </label>
            </Flex>

            <Flex gap="2" align="center">
              <Checkbox
                checked={filters.hideZeroProximityColumns}
                onCheckedChange={handleFilterChange("hideZeroProximityColumns")}
                id="hide-zero-proximity-columns"
              />
              <label htmlFor="hide-zero-proximity-columns">
                <Text size="2">Hide zero Proximities</Text>
              </label>
            </Flex>

            <Flex gap="2" align="center">
              <Checkbox
                checked={filters.hideZeroValues}
                onCheckedChange={handleFilterChange("hideZeroValues")}
                id="hide-zero-values"
              />
              <label htmlFor="hide-zero-values">
                <Text size="2">Hide zero cell</Text>
              </label>
            </Flex>

            <Flex gap="2" align="center">
              <Checkbox
                checked={filters.isTransposed}
                onCheckedChange={(checked) => {
                  setFilters((prev) => ({
                    ...prev,
                    isTransposed: Boolean(checked),
                  }));
                  blurActiveElement();
                }}
                id="transpose-table"
              />
              <label htmlFor="transpose-table">
                <Text size="2">Transpose</Text>
              </label>
            </Flex>
          </Flex>
        </section>

        {intersectedProxs.length === 0 || sscgs.length === 0 || !isExpanded ? (
          <div className="p-4" role="status" aria-live="polite">
            <Text size="2" color="gray">
              No data available. Please select a gondola change group with
              intersected proximities and SKU section change groups.
            </Text>
          </div>
        ) : (
          <>
            <section
              aria-label="SKU Section Change Group data table"
              className={
                isExpanded
                  ? "flex-grow overflow-hidden flex flex-col"
                  : "flex-grow"
              }
            >
              <div
                ref={tableContainerRef}
                className={`${COLORS.BORDER_GRAY} border rounded-lg overflow-auto ${
                  isExpanded ? "flex-grow" : "max-h-96"
                }`}
                style={{
                  scrollbarWidth: "auto",
                  scrollbarColor: `${COLORS.SCROLLBAR_THUMB} ${COLORS.SCROLLBAR_TRACK}`,
                }}
              >
                <table
                  className={`${COLORS.BG_WHITE_CLASS} relative border-collapse`}
                  style={{
                    tableLayout: "auto",
                    width: "auto",
                    marginBottom: "8px",
                  }}
                  role="table"
                  aria-rowcount={visibleRows.length + 1}
                  aria-colcount={visibleColumns.length + 1}
                >
                  <thead
                    className={`${COLORS.BG_GRAY_100_CLASS} z-20 sticky top-0`}
                  >
                    <tr role="row" className="border-b border-gray-200">
                      <th
                        className={`sticky left-0 z-30 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                        style={{
                          ...STYLE_OBJECTS.columnHeaderUnselected,
                          borderRight: "1px solid #e5e7eb",
                          borderBottom: "1px solid #e5e7eb",
                          minWidth: "100px",
                          width: "100px",
                          padding: "8px",
                        }}
                        scope="col"
                        role="columnheader"
                      >
                        Header
                      </th>
                      {memoizedColumnHeaders}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, rowIndex) => (
                      <TableRow
                        key={row.id}
                        rowIndex={rowIndex}
                        row={row}
                        visibleColumns={visibleColumns}
                        filters={filters}
                        selectionState={selectionState}
                        handleRowHeaderClick={handleRowHeaderClick}
                        handleCellClick={handleCellClick}
                        cellValuesCache={cellValuesCache}
                        getRowSkuSectionImage={getRowSkuSectionImage}
                        externallySelectedTableId={externallySelectedTableId}
                        copiedStates={copiedStates}
                        handleProximityCopy={handleProximityCopy}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div
                className="mt-2 text-xs text-gray-500 flex-shrink-0"
                role="status"
                aria-live="polite"
              >
                Showing {visibleRows.length} of {tableData.rows.length}{" "}
                {filters.isTransposed ? "proximity" : "SSCG"} rows and{" "}
                {visibleColumns.length} of {tableData.columns.length}{" "}
                {filters.isTransposed ? "SSCG" : "proximity"} columns
              </div>
            </section>
          </>
        )}
      </Flex>
    </>
  );
}
